import type { CharacterStay } from "@/data/rail-branches";
import { markerCircleRadius } from "../../marker/MarkerMetrics/circleRadius";
import { createFrameMemo } from "../FrameMemo/memo";
import { staysAtPlace } from "../StayAt/byPlace";
import {
  MARKER_DIAMETER,
  MARKER_GAP,
  markerPackStep,
} from "../../marker/MarkerMetrics/metrics";

export interface PlaceGroup {
  placeId: string;
  placeLabel?: string;
  characterIds: string[];
  /** When this part first held someone who is still there. */
  startMs: number;
}

/** People sharing one part of a site. Different parts are different groups. */
export function placeGroupsAt(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  playheadMs: number,
): PlaceGroup[] {
  return groupsMemo(stays, playheadMs, rootPlaceId, () => (
    computePlaceGroups(stays, rootPlaceId, playheadMs)
  ));
}

const groupsMemo = createFrameMemo<readonly CharacterStay[], PlaceGroup[]>();
const changeMemo = createFrameMemo<readonly CharacterStay[], number | undefined>();

function computePlaceGroups(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  playheadMs: number,
): PlaceGroup[] {
  const byPlace = new Map<string, PlaceGroup>();
  for (const stay of chosenStays(stays, rootPlaceId, playheadMs)) {
    const placeId = stay.placeId ?? stay.rootPlaceId;
    const group = byPlace.get(placeId) ?? {
      placeId,
      ...(stay.placeLabel === undefined ? {} : { placeLabel: stay.placeLabel }),
      characterIds: [],
      startMs: stay.startMs,
    };
    group.characterIds.push(stay.characterId);
    group.startMs = Math.min(group.startMs, stay.startMs);
    byPlace.set(placeId, group);
  }
  const groups = [...byPlace.values()];
  for (const group of groups) {
    group.characterIds.sort((a, b) => a.localeCompare(b));
  }
  groups.sort(
    (a, b) => a.startMs - b.startMs || a.placeId.localeCompare(b.placeId),
  );
  return groups;
}

/** Clear space between two groups on the same line. */
const GROUP_GAP = MARKER_GAP * 2;

/** Shift so two circles on one line do not sit on top of each other. */
export function groupCenterDx(
  groups: readonly PlaceGroup[],
  placeId: string,
  row: boolean,
): number {
  if (groups.length <= 1) {
    return 0;
  }
  const gap = GROUP_GAP;
  const widths = groups.map((group) => groupSpan(group.characterIds.length, row));
  const total = widths.reduce((sum, width) => sum + width, 0)
    + gap * (groups.length - 1);
  let cursor = -total / 2;
  for (let i = 0; i < groups.length; i++) {
    const width = widths[i] ?? 0;
    const center = cursor + width / 2;
    if (groups[i]?.placeId === placeId) {
      return center;
    }
    cursor += width + gap;
  }
  return 0;
}

/**
 * Latest moment who shares a part changed, inside the curve.
 * A move between parts that both stay occupied still counts.
 */
export function recentPlaceGroupChangeMs(
  stays: readonly CharacterStay[],
  playheadMs: number,
  curveMs: number,
): number | undefined {
  return changeMemo(stays, playheadMs, String(curveMs), () => (
    computeRecentChange(stays, playheadMs, curveMs)
  ));
}

function computeRecentChange(
  stays: readonly CharacterStay[],
  playheadMs: number,
  curveMs: number,
): number | undefined {
  let latest: number | undefined;
  for (const stay of stays) {
    for (const at of [stay.startMs, stay.endMs]) {
      if (at <= 0 || playheadMs < at || playheadMs - at >= curveMs) {
        continue;
      }
      if (!membershipChanged(stays, stay.rootPlaceId, at)) {
        continue;
      }
      if (latest === undefined || at > latest) {
        latest = at;
      }
    }
  }
  return latest;
}

function chosenStays(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  playheadMs: number,
): CharacterStay[] {
  const chosen = new Map<string, CharacterStay>();
  for (const stay of staysAtPlace(stays, rootPlaceId)) {
    // Vehicles have their own row, so they take no seat in a group.
    if (stay.vehicle === true || playheadMs < stay.startMs || playheadMs >= stay.endMs) {
      continue;
    }
    const current = chosen.get(stay.characterId);
    if (current === undefined || stay.startMs === playheadMs) {
      chosen.set(stay.characterId, stay);
    }
  }
  return [...chosen.values()];
}

function membershipChanged(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  at: number,
): boolean {
  const before = membershipKey(stays, rootPlaceId, at - 1);
  const after = membershipKey(stays, rootPlaceId, at);
  if (before !== after) {
    return true;
  }
  return after !== membershipKey(stays, rootPlaceId, at + 1);
}

function membershipKey(
  stays: readonly CharacterStay[],
  rootPlaceId: string,
  playheadMs: number,
): string {
  return placeGroupsAt(stays, rootPlaceId, playheadMs)
    .map((group) => `${group.placeId}:${group.characterIds.join(",")}`)
    .join("|");
}

function groupSpan(count: number, row: boolean): number {
  if (count <= 1) {
    return MARKER_DIAMETER;
  }
  if (row) {
    return (count - 1) * markerPackStep() + MARKER_DIAMETER;
  }
  return markerCircleRadius(count) * 2 + MARKER_DIAMETER;
}
